import React from 'react';
import { Highlighter, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { HIGHLIGHT_COLORS } from './AnnotationToolbar';

export function AnnotationSidebar({
  highlights, marginNotes, drawings,
  onDeleteHighlight, onDeleteNote, onEditNote,
}) {
  return (
    <Card className="p-4 sticky top-24">
      <h3 className="font-serif text-lg font-normal text-stone-900 mb-4">
        Your Annotations
      </h3>

      {/* Highlights */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
          <Highlighter className="h-4 w-4" />
          Highlights ({highlights.length})
        </h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {highlights.map((h) => {
            const colorClass = HIGHLIGHT_COLORS.find(c => c.value === h.color);
            return (
              <div
                key={h.id}
                className={`p-2 rounded text-xs ${colorClass?.bg || 'bg-yellow-200'} ${colorClass?.text || 'text-yellow-900'}`}
              >
                <div className="flex justify-between items-start">
                  <span className="line-clamp-2">{h.text}</span>
                  <button
                    onClick={() => onDeleteHighlight(h.id)}
                    className="ml-2 opacity-50 hover:opacity-100"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
          {highlights.length === 0 && (
            <p className="text-xs text-stone-400">No highlights yet</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Notes ({marginNotes.length})
        </h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {marginNotes.map((n) => (
            <div key={n.id} className="p-2 bg-yellow-50 rounded text-xs border border-yellow-200">
              <div className="flex justify-between items-start">
                <span className="line-clamp-3">{n.content}</span>
                <button
                  onClick={() => onDeleteNote(n.id)}
                  className="ml-2 opacity-50 hover:opacity-100"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
          {marginNotes.length === 0 && (
            <p className="text-xs text-stone-400">No notes yet</p>
          )}
        </div>
      </div>

      {/* Drawings */}
      <div>
        <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
          <Pencil className="h-4 w-4" />
          Drawings ({drawings.length})
        </h4>
        {drawings.length > 0 ? (
          <p className="text-xs text-stone-500">{drawings.length} stroke(s) recorded</p>
        ) : (
          <p className="text-xs text-stone-400">No drawings yet</p>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-6 p-3 bg-stone-50 rounded-lg text-xs text-stone-600">
        <p className="font-medium mb-1">How to annotate:</p>
        <ul className="space-y-1">
          <li>• <strong>Highlight:</strong> Select text to highlight</li>
          <li>• <strong>Note:</strong> Click anywhere to add a margin note</li>
          <li>• <strong>Draw:</strong> Toggle drawing mode and draw freely</li>
        </ul>
      </div>
    </Card>
  );
}
